import React from 'react'
import { BuildingOffice2Icon, ExclamationTriangleIcon } from '@heroicons/react/24/outline'
import { Button, Heading, LoadingSpinner, Text } from '../../components/ui'
import { comercialAccountHealthService } from '../../services/comercialAccountHealth'
import type { Client } from '../../types/clients'
import type { AccountHealthLevel, AccountHealthPillar } from '../../types/comercialAccountHealth'
import { ACCOUNT_HEALTH_LEVEL_IDS } from '../../types/comercialAccountHealth'
import { scoreToLevel, sumSelectedPoints } from '../../utils/comercialAccountHealth'

type HealthUiMeta = {
  hint: string
  color: string
  ring: string
  text: string
}

const DEFAULT_LEVEL_UI: HealthUiMeta = {
  hint: '',
  color: 'bg-gray-400',
  ring: 'stroke-gray-400',
  text: 'text-gray-700 dark:text-gray-300',
}

/** Cores/hints por UUID do seed (comercial_17). */
const LEVEL_UI_BY_ID: Record<string, HealthUiMeta> = {
  [ACCOUNT_HEALTH_LEVEL_IDS.saudavel]: {
    hint: 'Conta estável',
    color: 'bg-emerald-500',
    ring: 'stroke-emerald-500',
    text: 'text-emerald-600 dark:text-emerald-400',
  },
  [ACCOUNT_HEALTH_LEVEL_IDS.atencao]: {
    hint: 'Acompanhar',
    color: 'bg-amber-400',
    ring: 'stroke-amber-400',
    text: 'text-amber-600 dark:text-amber-400',
  },
  [ACCOUNT_HEALTH_LEVEL_IDS.risco]: {
    hint: 'Agir',
    color: 'bg-orange-500',
    ring: 'stroke-orange-500',
    text: 'text-orange-600 dark:text-orange-400',
  },
  [ACCOUNT_HEALTH_LEVEL_IDS.critico]: {
    hint: 'Prioridade',
    color: 'bg-red-600',
    ring: 'stroke-red-600',
    text: 'text-red-600 dark:text-red-400',
  },
}

function levelUi(level: AccountHealthLevel | null): HealthUiMeta {
  if (!level) return DEFAULT_LEVEL_UI
  return LEVEL_UI_BY_ID[level.id] ?? DEFAULT_LEVEL_UI
}

function getErrorMessage(error: unknown): string {
  if (error instanceof Error) return error.message
  if (typeof error === 'string') return error
  if (error && typeof error === 'object' && 'message' in error) {
    const message = (error as { message?: unknown }).message
    if (typeof message === 'string' && message.trim()) return message
  }
  return 'Erro inesperado ao carregar os parâmetros de saúde.'
}

function ScoreGauge({
  total,
  level,
}: {
  total: number | null
  level: AccountHealthLevel | null
}) {
  const pct = total == null ? 0 : Math.min(100, Math.max(0, total))
  const radius = 54
  const circumference = 2 * Math.PI * radius
  const offset = circumference - (pct / 100) * circumference
  const ui = levelUi(level)

  return (
    <div className="relative mx-auto h-36 w-36">
      <svg className="h-full w-full -rotate-90" viewBox="0 0 128 128" aria-hidden>
        <circle
          cx="64"
          cy="64"
          r={radius}
          fill="none"
          className="stroke-gray-200 dark:stroke-gray-700"
          strokeWidth="10"
        />
        <circle
          cx="64"
          cy="64"
          r={radius}
          fill="none"
          className={ui.ring}
          strokeWidth="10"
          strokeLinecap="round"
          strokeDasharray={circumference}
          strokeDashoffset={offset}
        />
      </svg>
      <div className="absolute inset-0 flex flex-col items-center justify-center">
        <Text size="sm" color="muted">
          Pontuação
        </Text>
        <span className={`text-2xl font-semibold ${ui.text}`}>
          {total == null ? '—' : total}
        </span>
        {level && <span className={`text-xs font-medium ${ui.text}`}>{level.label}</span>}
      </div>
    </div>
  )
}

type ClienteContaSaudeTabProps = {
  client: Client
  nameDisplay: string
  cnpjDisplay: string
  cityStateDisplay: string
}

const ClienteContaSaudeTab: React.FC<ClienteContaSaudeTabProps> = ({
  client,
  nameDisplay,
  cnpjDisplay,
  cityStateDisplay,
}) => {
  const [levels, setLevels] = React.useState<AccountHealthLevel[]>([])
  const [pillars, setPillars] = React.useState<AccountHealthPillar[]>([])
  const [loading, setLoading] = React.useState(true)
  const [loadError, setLoadError] = React.useState<string | null>(null)
  const [selectedOptionByPillarId, setSelectedOptionByPillarId] = React.useState<
    Record<string, string>
  >({})
  const [savedHint, setSavedHint] = React.useState<string | null>(null)

  React.useEffect(() => {
    let cancelled = false
    const load = async () => {
      setLoading(true)
      setLoadError(null)
      try {
        const config = await comercialAccountHealthService.listConfig()
        if (cancelled) return
        setLevels(config.levels)
        setPillars(config.pillars.filter((p) => p.is_active))
      } catch (error) {
        if (cancelled) return
        setLoadError(getErrorMessage(error))
        setLevels([])
        setPillars([])
      } finally {
        if (!cancelled) setLoading(false)
      }
    }
    void load()
    return () => {
      cancelled = true
    }
  }, [])

  const activePillars = pillars.filter((p) => p.is_active)
  const selectedCount = activePillars.filter((p) => selectedOptionByPillarId[p.id]).length
  const allSelected = activePillars.length > 0 && selectedCount === activePillars.length

  const total = allSelected
    ? sumSelectedPoints(activePillars, selectedOptionByPillarId)
    : null

  const level = total == null ? null : scoreToLevel(total, levels)

  const setPillarOption = (pillarId: string, optionId: string) => {
    setSavedHint(null)
    setSelectedOptionByPillarId((prev) => ({ ...prev, [pillarId]: optionId }))
  }

  const handleSave = () => {
    if (!allSelected) {
      setSavedHint('Selecione todos os pilares antes de salvar.')
      return
    }
    setSavedHint('Avaliação registrada localmente. Persistência em breve.')
  }

  if (loading) {
    return (
      <div className="flex items-center justify-center py-12">
        <LoadingSpinner />
      </div>
    )
  }

  if (loadError) {
    return (
      <div className="bg-red-50 border border-red-200 text-red-700 px-4 py-3 rounded-lg dark:bg-red-900/20 dark:border-red-800 dark:text-red-300">
        <Text size="sm">{loadError}</Text>
      </div>
    )
  }

  if (activePillars.length === 0) {
    return (
      <div className="bg-white dark:bg-gray-800 rounded-lg shadow p-6 text-center">
        <Text size="sm" color="muted">
          Parâmetros de saúde não encontrados. Cadastre-os em Comercial → Cadastro → Clientes
          (execute o SQL comercial_17_account_health_parameters.sql se ainda não rodou).
        </Text>
      </div>
    )
  }

  return (
    <div className="grid grid-cols-1 xl:grid-cols-[1fr_20rem] gap-3 items-start">
      <div className="space-y-3 min-w-0">
        <div className="bg-white dark:bg-gray-800 rounded-lg shadow px-4 py-3">
          <div className="flex items-center gap-2.5 min-w-0">
            <div className="flex h-10 w-10 shrink-0 items-center justify-center rounded-lg bg-[#008B95]/10 dark:bg-teal-900/40">
              <BuildingOffice2Icon className="h-5 w-5 text-[#008B95] dark:text-teal-400" />
            </div>
            <div className="min-w-0">
              <Heading as="h2" size="xl" className="text-gray-900 dark:text-white break-words">
                {nameDisplay}
              </Heading>
              <Text size="sm" color="muted" className="mt-0.5">
                CNPJ {cnpjDisplay}
                {client.id_scgp ? ` · SCGP ${client.id_scgp}` : ''}
                {' · '}
                {cityStateDisplay}
              </Text>
            </div>
          </div>
        </div>

        <div className="bg-white dark:bg-gray-800 rounded-lg shadow px-4 py-3 space-y-4">
          <div>
            <div className="flex items-start justify-between gap-3">
              <Heading as="h2" size="xl" className="text-gray-900 dark:text-white">
                Avaliação da conta
              </Heading>
              <Text size="sm" color="muted" className="shrink-0 pt-1">
                Último contato: —
              </Text>
            </div>
            <Text size="sm" color="muted" className="mt-1">
              Após cada contato, registre uma resposta em cada pilar. O sistema soma os pontos
              (0–100) e calcula automaticamente o status da conta.
            </Text>
          </div>

          {activePillars.map((pillar) => {
            const selectedId = selectedOptionByPillarId[pillar.id]
            const options = pillar.options.filter((o) => o.is_active)
            return (
              <div
                key={pillar.id}
                className="border-t border-gray-200 dark:border-gray-700 pt-4"
              >
                <Heading as="h3" size="lg" className="text-gray-900 dark:text-white">
                  {pillar.title}
                </Heading>
                <Text size="sm" color="muted" className="mt-1 mb-3">
                  {pillar.question}
                </Text>
                <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-5 gap-2">
                  {options.map((opt) => {
                    const isActive = selectedId === opt.id
                    return (
                      <button
                        key={opt.id}
                        type="button"
                        aria-pressed={isActive}
                        onClick={() => setPillarOption(pillar.id, opt.id)}
                        className={`flex min-h-[4.5rem] flex-col items-center justify-center gap-1 rounded-lg border px-2 py-2 text-center transition-all ${
                          isActive
                            ? 'border-[#008B95] bg-[#008B95]/10 ring-2 ring-[#008B95] dark:bg-teal-900/40'
                            : 'border-gray-200 bg-gray-50 hover:border-gray-300 hover:bg-gray-100 dark:border-gray-600 dark:bg-gray-700/50 dark:hover:bg-gray-700'
                        }`}
                      >
                        {opt.emoji && (
                          <span className="text-xl leading-none" aria-hidden>
                            {opt.emoji}
                          </span>
                        )}
                        <span className="text-xs font-medium leading-snug text-gray-800 dark:text-gray-100">
                          {opt.label}
                        </span>
                      </button>
                    )
                  })}
                </div>
              </div>
            )
          })}
        </div>
      </div>

      <aside className="xl:sticky xl:top-4 space-y-3 min-w-0">
        <div className="bg-white dark:bg-gray-800 rounded-lg shadow px-4 py-4">
          <Heading as="h3" size="lg" className="text-gray-900 dark:text-white mb-3">
            Resultado calculado
          </Heading>

          <ScoreGauge total={total} level={level} />

          <ul className="mt-4 space-y-2">
            {levels.map((item) => {
              const ui = levelUi(item)
              const isActive = level?.id === item.id
              return (
                <li
                  key={item.id}
                  className={`flex items-center justify-between gap-2 rounded-md px-1.5 py-1 ${
                    isActive ? 'bg-gray-100 dark:bg-gray-700/60' : ''
                  }`}
                >
                  <span className="flex items-center gap-2 min-w-0">
                    <span className={`h-2.5 w-2.5 shrink-0 rounded-full ${ui.color}`} />
                    <Text
                      size="sm"
                      className={
                        isActive
                          ? `${ui.text} font-medium`
                          : 'text-gray-700 dark:text-gray-300'
                      }
                    >
                      {item.label}
                    </Text>
                  </span>
                  <Text size="xs" color="muted" className="shrink-0">
                    {item.min_score}–{item.max_score}
                    {ui.hint ? ` · ${ui.hint}` : ''}
                  </Text>
                </li>
              )
            })}
          </ul>

          <div className="mt-4 space-y-2 border-t border-gray-200 dark:border-gray-700 pt-3">
            {activePillars.map((pillar) => {
              const optionId = selectedOptionByPillarId[pillar.id]
              const selectedOpt = pillar.options.find((o) => o.id === optionId)
              const done = Boolean(selectedOpt)
              return (
                <div key={pillar.id} className="flex items-start gap-2">
                  {done && selectedOpt ? (
                    <span className="text-base leading-5 shrink-0" aria-hidden>
                      {selectedOpt.emoji || '✓'}
                    </span>
                  ) : (
                    <ExclamationTriangleIcon className="h-5 w-5 shrink-0 text-amber-500" />
                  )}
                  <div className="min-w-0">
                    <Text size="sm" className="text-gray-900 dark:text-gray-100">
                      {pillar.title.replace(/^Pilar \d+ — /, '')}
                    </Text>
                    <Text size="xs" color="muted">
                      {done ? (selectedOpt?.label ?? '') : 'Pendente'}
                    </Text>
                  </div>
                </div>
              )
            })}
          </div>

          <Button type="button" variant="solid" className="mt-4 w-full" onClick={handleSave}>
            Salvar avaliação
          </Button>

          <Text size="xs" color="muted" className="mt-2 text-center">
            Persistência no histórico da conta em breve.
          </Text>

          {savedHint && (
            <Text size="sm" color="muted" className="mt-2 text-center">
              {savedHint}
            </Text>
          )}
        </div>
      </aside>
    </div>
  )
}

export default ClienteContaSaudeTab
